import React, { useState } from 'react';
import { FiCalendar } from 'react-icons/fi';

import moment from 'moment';

import ApplicationOverlayContent from 'components/ApplicationLayout/ApplicationOverlayContent';
import BottomSheet from 'components/BottomSheet';
import DateRangePicker from 'components/DateRangePicker';
import Loader from 'components/Loader';
import ModalHeaderSubtitle from 'components/ModalHeaderSubtitle';
import { Desktop, Mobile } from 'components/Responsive';
import Search from 'components/Search';
import BookingSelectDate, { RenderProps as BookingSelectRenderProps } from 'components/Selects/BookingSelectDate';
import FormSelect from 'components/Selects/FormSelect';
import { SearchParams, SearchParamsHistory } from 'components/TripSearch/models';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { TravelType } from 'models/enums/travelType';
import { RequestTripType } from 'models/enums/tripType';
import { TravelStatus } from 'models/travelStatus';
import { dateFormatWeekend } from 'utils/generic';
import { statusTranslation } from 'utils/statusTag';

import { Header, Close, Container, Fields, Field, SearchButtonContainer, SearchButton } from './styles';

interface Props {
  show: boolean;
  onClose: () => void;
  onSearch: (params: SearchParams | SearchParamsHistory) => void;
  isLoading: boolean;
  defaultParams?: SearchParams | SearchParamsHistory;
  withItineraryProps: boolean;
}

const TripSearch = ({ show, onClose, onSearch, isLoading, defaultParams, withItineraryProps }: Props) => {
  const { t } = useStoreTranslation();
  const [initDate, setInitDate] = useState<Date | undefined>(
    defaultParams?.initDate ? new Date(defaultParams?.initDate) : undefined,
  );
  const [endDate, setEndDate] = useState<Date | undefined>(
    defaultParams?.endDate ? new Date(defaultParams?.endDate) : undefined,
  );
  const [tripType, setTripType] = useState<string | undefined>(defaultParams?.tripType ?? 'all');
  const [distanceRestriction, setDistanceRestriction] = useState<string | undefined>(
    defaultParams?.distanceRestriction ?? 'undefined',
  );
  const [travelStatus, setTravelStatus] = useState<string | undefined>(defaultParams?.travelStatus ?? 'all');
  const [travelItineraryId, setTravelItineraryId] = useState<string | undefined>(
    !!defaultParams?.travelItineraryId ? defaultParams?.travelItineraryId : undefined,
  );

  const handleChangeDateSelect = (dates: { initialDate: Date; finalDate: Date }) => {
    setInitDate(dates?.initialDate);
    setEndDate(dates?.finalDate);
  };

  const renderInitDateSelect = () => (
    <BookingSelectDate
      label={t('myTrips.search.initDate.label')}
      value={dateFormatWeekend(initDate)}
      onChange={handleChangeDateSelect}
      icon={<FiCalendar />}
      testId="mytrips-search-init-date"
    >
      {(selectProps: BookingSelectRenderProps) => (
        <DateRangePicker
          {...selectProps}
          type="trip"
          onlyDate={false}
          currentInitialDate={initDate}
          currentFinalDate={endDate}
          allDate={true}
        />
      )}
    </BookingSelectDate>
  );

  const renderEndDateSelect = () => (
    <BookingSelectDate
      disabled={false}
      label={t('myTrips.search.endDate.label')}
      value={dateFormatWeekend(endDate)}
      onChange={handleChangeDateSelect}
      icon={<FiCalendar />}
      testId="mytrips-search-end-date"
    >
      {(selectProps: BookingSelectRenderProps) => (
        <DateRangePicker
          {...selectProps}
          type="trip"
          onlyDate={false}
          currentInitialDate={initDate}
          currentFinalDate={endDate}
          editingInitialDate={false}
          allDate={true}
        />
      )}
    </BookingSelectDate>
  );

  const renderDestinationType = () => {
    const options = [
      { label: t('myTrips.search.distanceRestriction.options.all'), value: 'undefined' },
      { label: t('myTrips.search.distanceRestriction.options.domestic'), value: 'domestic' },
      { label: t('myTrips.search.distanceRestriction.options.international'), value: 'international' },
    ];

    return (
      <Field columns={3}>
        <Field.Label>{t('myTrips.search.distanceRestriction.label')}:</Field.Label>
        <Field.Input>
          <FormSelect
            name="distanceRestriction"
            onChange={(value) => setDistanceRestriction(value)}
            options={options}
            defaultValue={defaultParams?.distanceRestriction ?? 'undefined'}
          />
        </Field.Input>
      </Field>
    );
  };

  const renderTripType = () => {
    const options = [
      { label: t('myTrips.search.product.options.all'), value: 'all' },
      { label: t('myTrips.search.product.options.flight'), value: TravelType.Flight },
      { label: t('myTrips.search.product.options.vehicle'), value: TravelType.Vehicle },
      { label: t('myTrips.search.product.options.hotel'), value: TravelType.Hotel },
    ];

    return (
      <Field columns={3}>
        <Field.Label>{t('myTrips.search.product.label')}:</Field.Label>
        <Field.Input>
          <FormSelect
            name="tripType"
            onChange={(value) => setTripType(value)}
            options={options}
            defaultValue={defaultParams?.tripType ?? 'all'}
          />
        </Field.Input>
      </Field>
    );
  };

  const renderTravelStatus = () => {
    const options = Object.values(TravelStatus)
      .map((status) => ({ label: statusTranslation(status, t), value: status as string }))
      .filter((opt) => !!opt.value && opt.value !== opt.label)
      .concat([{ label: t('myTrips.search.travelStatus.options.all'), value: 'all' }]);
    return (
      <Field columns={3}>
        <Field.Label>{t('myTrips.search.travelStatus.label')}:</Field.Label>
        <Field.Input>
          <FormSelect
            name="travelStatus"
            onChange={(value) => setTravelStatus(value)}
            options={options}
            defaultValue={defaultParams?.travelStatus ?? 'all'}
          />
        </Field.Input>
      </Field>
    );
  };

  const renderTravelItineraryId = () => (
    <Field isFlex={true}>
      <Field.Label className="search-tvl-id">{t('myTrips.search.travelItineraryId.label')}:</Field.Label>
      <Search
        className="search-tvl-id"
        onSearch={handleOnSearchTvlId}
        placeholder={t('myTrips.search.travelItineraryId.placeholder')}
        initialValue={!!defaultParams?.travelItineraryId ? defaultParams?.travelItineraryId : undefined}
      />
    </Field>
  );

  const handleOnSearchTvlId = (value: string) => {
    setTravelItineraryId(!!value ? value : undefined);
  };

  const renderContent = () => (
    <Container>
      <Fields>
        {renderDestinationType()}
        {renderTripType()}
        {withItineraryProps && renderTravelStatus()}
        {withItineraryProps && renderTravelItineraryId()}
        <Field>{t('myTrips.search.period')}:</Field>
        <Field columns={2}>
          {renderInitDateSelect()}
          {renderEndDateSelect()}
        </Field>
      </Fields>
      <SearchButtonContainer>
        <SearchButton
          disabled={false}
          onClick={() => {
            onSearch({
              destination: undefined,
              distanceRestriction:
                distanceRestriction === RequestTripType.Domestic ||
                distanceRestriction === RequestTripType.International
                  ? distanceRestriction
                  : undefined,
              tripType: Object.values(TravelType).includes(tripType as TravelType)
                ? (tripType as TravelType)
                : undefined,
              initDate: initDate ? `${moment(initDate).format('YYYY-MM-DD')}T00:00:00` : undefined,
              endDate: endDate ? `${moment(endDate).format('YYYY-MM-DD')}T00:00:00` : undefined,
              travelStatus: Object.values(TravelStatus).includes(travelStatus as TravelStatus)
                ? (travelStatus as TravelStatus)
                : undefined,
              travelItineraryId: !!travelItineraryId ? travelItineraryId : undefined,
            });
          }}
          value={t('myTrips.search.button')}
          testId="mytrips-search-submit"
        />
      </SearchButtonContainer>
    </Container>
  );
  const LoaderDesktop = () => <div>Loader</div>;

  return (
    <>
      <Mobile>
        <BottomSheet show={show} onClose={onClose} noHeader zIndex={2}>
          <Header>
            <Header.Title>{t('myTrips.search.title')}</Header.Title>
            <Close onClick={onClose}>
              <Close.Icon />
            </Close>
          </Header>
          {isLoading ? <Loader fullScreen /> : renderContent()}
        </BottomSheet>
      </Mobile>
      <Desktop>
        <ApplicationOverlayContent show={show} onClose={onClose}>
          <Container>
            <ModalHeaderSubtitle
              title={t('myTrips.search.title')}
              subtitle={t('myTrips.search.text')}
              onClose={onClose}
            />
            {isLoading ? <LoaderDesktop /> : renderContent()}
          </Container>
        </ApplicationOverlayContent>
      </Desktop>
    </>
  );
};

export default TripSearch;
